import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import api from "../services/api";
import AppSkeleton from "../components/ui/AppSkeleton";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializedRef.current) {
      console.log("⏭️ AuthContext already initialized, skipping...");
      return;
    }

    const initializeAuth = () => {
      initializedRef.current = true;
      const startTime = Date.now();
      const minDelay = 600;

      let token = localStorage.getItem("token");
      let userData = localStorage.getItem("user");

      // If localStorage is empty, try sessionStorage as fallback
      if (!token || !userData) {
        const sessionToken = sessionStorage.getItem("token");
        const sessionUser = sessionStorage.getItem("user");
        if (sessionToken && sessionUser) {
          console.log("⚠️ localStorage empty, restoring from sessionStorage");
          try {
            localStorage.setItem("token", sessionToken);
            localStorage.setItem("user", sessionUser);
            token = sessionToken;
            userData = sessionUser;
          } catch (e) {
            console.error("❌ Failed to restore from sessionStorage:", e);
          }
        }
      }

      console.log("AuthContext: Initialization started", {
        hasToken: !!token,
        hasUserData: !!userData,
      });

      const processAuth = async () => {
        if (token && userData) {
          try {
            const parsedUser = JSON.parse(userData);

            if (parsedUser.id && parsedUser.email) {
              if (!parsedUser.role) parsedUser.role = "EMPLOYEE";
              parsedUser.role = parsedUser.role.toUpperCase();
              localStorage.setItem("user", JSON.stringify(parsedUser));

              setUser(parsedUser);
              api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

              // Background refresh: fetch fresh role + navPermissions from DB
              // so role changes by admin take effect without requiring re-login.
              try {
                const profileRes = await api.get("/profile");
                const freshUser = profileRes.data?.user;
                if (freshUser && freshUser.id && freshUser.email) {
                  const merged = {
                    ...parsedUser,
                    ...freshUser,
                    role: (freshUser.role || parsedUser.role || "EMPLOYEE").toUpperCase(),
                    // Explicit null from API must clear cached nav overrides (role defaults)
                    navPermissions: Object.prototype.hasOwnProperty.call(
                      freshUser,
                      "navPermissions"
                    )
                      ? freshUser.navPermissions
                      : parsedUser.navPermissions,
                  };
                  setUser(merged);
                  localStorage.setItem("user", JSON.stringify(merged));
                  console.log("✅ AuthContext: Role refreshed from server →", merged.role);
                }
              } catch (refreshErr) {
                console.warn("⚠️ Profile refresh failed (using cached role):", refreshErr.message);
              }
            } else {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
            }
          } catch (err) {
            console.error("❌ Failed to parse user data", err);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        } else {
          console.warn("⚠️ AuthContext: No token or user data found");
        }

        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, minDelay - elapsed);

        setTimeout(() => {
          setLoading(false);
          console.log("AuthContext: Loading complete");
        }, remainingDelay);
      };

      processAuth();
    };

    initializeAuth();
  }, []);

  // Watch for storage changes (e.g., from callback page)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("user");

      console.log("🔄 Storage change detected:", {
        hasToken: !!token,
        hasUserData: !!userData,
        currentUser: !!user,
      });

      if (token && userData && !user) {
        console.log("🔄 Auth data detected, updating user from callback...");
        try {
          const parsedUser = JSON.parse(userData);
          console.log("📋 Parsed user:", {
            id: parsedUser.id,
            email: parsedUser.email,
            role: parsedUser.role,
          });

          if (parsedUser.id && parsedUser.email) {
            // Normalize role to uppercase
            if (!parsedUser.role) {
              parsedUser.role = "EMPLOYEE";
            } else {
              parsedUser.role = parsedUser.role.toUpperCase();
            }
            localStorage.setItem("user", JSON.stringify(parsedUser));
            setUser(parsedUser);
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            console.log("✅ User authenticated from callback", {
              role: parsedUser.role,
            });
          } else {
            console.error("❌ Invalid user data structure in callback");
          }
        } catch (err) {
          console.error("❌ Failed to parse callback user data", err);
        }
      }
    };

    // Listen for storage changes from other tabs/windows
    window.addEventListener("storage", handleStorageChange);

    // Also poll localStorage periodically while on callback page
    // (storage event won't fire in same tab that made the change)
    const isCallbackPage = window.location.pathname.includes(
      "/auth/outlook/callback"
    );
    let pollInterval;

    if (isCallbackPage && !user) {
      console.log("📡 Starting localStorage polling on callback page...");
      pollInterval = setInterval(() => {
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");
        console.log("🔍 Polling check:", {
          hasToken: !!token,
          hasUserData: !!userData,
          currentUser: !!user,
        });

        if (token && userData && !user) {
          console.log("✅ Found auth data during polling, updating...");
          handleStorageChange();
          clearInterval(pollInterval);
        }
      }, 200); // Check every 200ms for faster detection
    }

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { token, user: userData } = response.data;

      const normalizedUser = {
        ...userData,
        role: (userData?.role || "EMPLOYEE").toString().toUpperCase(),
      };

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setUser(normalizedUser);

      // Check for pending leave action after login
      const pendingLeaveAction = localStorage.getItem("pendingLeaveAction");
      if (pendingLeaveAction) {
        try {
          const actionData = JSON.parse(pendingLeaveAction);
          if (actionData.type === "leave_action") {
            // Redirect to leave action page to process the saved action
            setTimeout(() => {
              window.location.href = `/leave-action?leaveId=${actionData.leaveId}&action=${actionData.action}`;
            }, 500);
            return { success: true };
          }
        } catch (err) {
          console.error("Failed to process pending leave action:", err);
        }
      }

      // Check for pending expense action after login
      const pendingExpenseAction = localStorage.getItem("pendingExpenseAction");
      if (pendingExpenseAction) {
        try {
          const actionData = JSON.parse(pendingExpenseAction);
          if (actionData.type === "expense_action") {
            // Redirect to expense action page to process the saved action
            setTimeout(() => {
              window.location.href = `/expense-action?expenseId=${actionData.expenseId}&action=${actionData.action}`;
            }, 500);
            return { success: true };
          }
        } catch (err) {
          console.error("Failed to process pending expense action:", err);
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Login failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const updateUser = (userData) => {
    if (userData) {
      const token = userData.token;
      const userWithoutToken = { ...userData };
      delete userWithoutToken.token;

      // Normalize role to uppercase
      const normalizedRole = userWithoutToken.role
        ? userWithoutToken.role.toUpperCase()
        : "EMPLOYEE";
      const userWithRole = {
        ...userWithoutToken,
        role: normalizedRole,
      };

      setUser(userWithRole);
      localStorage.setItem("user", JSON.stringify(userWithRole));

      if (token) {
        localStorage.setItem("token", token);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }

      console.log("✅ AuthContext updated:", {
        role: userWithRole.role,
        isAuthenticated: true,
      });

      // Check for pending leave action after user update
      const pendingLeaveAction = localStorage.getItem("pendingLeaveAction");
      if (pendingLeaveAction) {
        try {
          const actionData = JSON.parse(pendingLeaveAction);
          if (actionData.type === "leave_action") {
            console.log(
              "🔄 Pending leave action found, will redirect:",
              actionData
            );
            // Don't redirect here - let the component handle it
            // The redirect will happen in LeaveAction component
          }
        } catch (err) {
          console.error("Failed to parse pending leave action:", err);
        }
      }

      // Check for pending expense action after user update
      const pendingExpenseAction = localStorage.getItem("pendingExpenseAction");
      if (pendingExpenseAction) {
        try {
          const actionData = JSON.parse(pendingExpenseAction);
          if (actionData.type === "expense_action") {
            console.log(
              "🔄 Pending expense action found, will redirect:",
              actionData
            );
            // Don't redirect here - let the component handle it
            // The redirect will happen in ExpenseAction component
          }
        } catch (err) {
          console.error("Failed to parse pending expense action:", err);
        }
      }

      return userWithRole;
    }
    return null;
  };

  const isAuthenticated = !!user;

  if (loading) {
    return <AppSkeleton />;
  }

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, login, logout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
